import * as yup from 'yup';
import { majorEmailProviders } from './major-email-providers';

import {
  MAX_CHAR_EMAIL,
  MAX_CHAR_REGISTER_EMAIL,
  MAX_CHAR_SUPPORT_EMAIL,
  MAX_CHAR_BSP_SUPPORT_EMAIL,
  MAX_CHAR_SUPPORT_PHONE,
  MAX_CHAR_NAME,
  MAX_CHAR_FIRST_NAME,
  MAX_CHAR_LAST_NAME,
  MAX_CHAR_COMPANY_NAME,
  MIN_CHAR_PASSWORD,
  MAX_CHAR_PASSWORD,
  MAX_CHAR_ACCOUNT_ALIAS,
  MAX_CHAR_PROBLEM_DESCRIPTION,
  MAX_SUPPORT_EMAIL_NUMBER,
} from '../../utils/char-limit-constants';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const containsMajorEmailProvider = (string) => {
  return majorEmailProviders.has(string?.split('@')[1]);
};

/**
 * Password Regex:
 * User password requires both upper (A-Z) and lower case (a-z) characters,
 * and must have a combination of letters and numbers,
 * Accepts the following character set: @$!%*#?&
 * must be between 10 and 128 characters long
 */
export const registrationValidation = yup.object({
  email: yup
    .string()
    .required('validations.missingEmail')
    .max(MAX_CHAR_REGISTER_EMAIL, 'validations.maxCharEmailRegister')
    .matches(emailRegex, 'validations.invalidEmail'),
  termsAndConditions: yup
    .boolean()
    .required('validations.acceptTermsConditions')
    .oneOf([true], 'validations.acceptTermsConditions'),
  name: yup
    .string()
    .required('validations.missingName')
    .max(MAX_CHAR_NAME, 'validations.maxCharName'),
  country: yup.string().required('validations.missingCountry'),
  password: yup
    .string()
    .required('validations.missingPassword')
    .min(MIN_CHAR_PASSWORD, 'validations.minLengthPassword')
    .max(MAX_CHAR_PASSWORD, 'validations.maxLengthPassword')
    .matches(/[a-z]+/, 'validations.requiresLowerCaseCharPassword')
    .matches(/[A-Z]+/, 'validations.requiresUpperCaseCharPassword')
    .matches(/^.*[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~].*$/, 'validations.specialCharPassword')
    .matches(/\d+/, 'validations.requiresNumericCharPassword'),
  confirmPassword: yup
    .string()
    .required('validations.missingConfirmPassword')
    .oneOf([yup.ref('password'), null], 'validations.notMatchingPasswords'),
  recaptcha: yup.string().required('validations.missingRecaptcha').nullable(true),
});

export const loginValidation = yup.object({
  email: yup
    .string()
    .required('validations.missingEmail')
    .max(MAX_CHAR_EMAIL, 'validations.maxCharEmail')
    .matches(emailRegex, 'validations.invalidEmail'),
  password: yup.string().required('validations.missingPassword'),
});

export const forgotPasswordValidation = yup.object({
  email: yup
    .string()
    .required('validations.missingEmail')
    .max(MAX_CHAR_EMAIL, 'validations.maxCharEmail')
    .matches(emailRegex, 'validations.invalidEmail'),
});

export const resetPasswordValidation = yup.object({
  newPassword: yup
    .string()
    .required('validations.missingPassword')
    .min(MIN_CHAR_PASSWORD, 'validations.minLengthPassword')
    .max(MAX_CHAR_PASSWORD, 'validations.maxLengthPassword')
    .matches(/[a-z]+/, 'validations.requiresLowerCaseCharPassword')
    .matches(/[A-Z]+/, 'validations.requiresUpperCaseCharPassword')
    .matches(/^.*[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~].*$/, 'validations.specialCharPassword')
    .matches(/\d+/, 'validations.requiresNumericCharPassword'),
  confirmPassword: yup
    .string()
    .required('validations.missingNewConfirmPassword')
    .oneOf([yup.ref('newPassword'), null], 'validations.notMatchingPasswords'),
});

export const accountSetupValidation = yup.object({
  accountAlias: yup
    .string()
    .required('validations.missingAccountAlias')
    .max(MAX_CHAR_ACCOUNT_ALIAS, 'validations.maxCharAccountAlias'),
  region: yup.string().required('validations.missingRegion'),
});

export const mfaLoginInput = yup.object({
  confirmationCode: yup
    .number()
    .typeError('inputIsNumeric')
    .required('validations.oneTimePasswordMissingError'),
});

export const mfaResetInput = yup.object({
  resetCode: yup.string().required('validations.resetCodeMissingError'),
});

export const accountSelection = yup.object({
  selectedAccount: yup.string().required('validations.missingAccountSelection'),
});

export const resetConfirmationCodeValidation = yup.object({
  email: yup
    .string()
    .required('validations.missingEmail')
    .max(MAX_CHAR_EMAIL, 'validations.maxCharEmail')
    .matches(emailRegex, 'validations.invalidEmail'),
});

export const wsLoginValidation = yup.object({
  account: yup.string().required('wsCommon.missingAccount'),
  username: yup.string().required('wsCommon.missingUsername'),
  password: yup.string().required('validations.missingPassword'),
  useMFACheck: yup.boolean(),
  mfaCode: yup
    .string()
    .ensure()
    .when('useMFACheck', {
      is: true,
      then: yup.string().required('wsLogin.missingMfaCode'),
    }),
});

export const wsPasswordResetValidation = yup.object({
  account: yup.string().required('wsCommon.missingAccount'),
  username: yup.string().required('wsCommon.missingUsername'),
  recaptcha: yup.string().required('validations.missingRecaptcha').nullable(true),
});

export const wsPasswordUpdateValidation = yup.object({
  newPassword: yup
    .string()
    .required('validations.missingPassword')
    .min(MIN_CHAR_PASSWORD, 'validations.minLengthPassword')
    .max(MAX_CHAR_PASSWORD, 'validations.maxLengthPassword')
    .matches(/[a-z]+/, 'validations.requiresLowerCaseCharPassword')
    .matches(/[A-Z]+/, 'validations.requiresUpperCaseCharPassword')
    .matches(/^.*[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~].*$/, 'validations.specialCharPassword')
    .matches(/\d+/, 'validations.requiresNumericCharPassword'),
  confirmPassword: yup
    .string()
    .required('validations.missingConfirmPassword')
    .oneOf([yup.ref('newPassword'), null], 'validations.notMatchingPasswords'),
});

// Contains major email provider method
yup.addMethod(yup.string, 'majorProviders', function (message: string) {
  return this.test('major-providers', message, (value) => {
    if (value && !containsMajorEmailProvider(value?.toLowerCase())) {
      return true;
    }
    return false;
  });
});

// Duplicate tags validation method
yup.addMethod(yup.array, 'uniqueTag', function (message: string, pathName: string) {
  return this.test('uniqueTag', message, function (tags: string[] = []) {
    if (tags.length === new Set(tags).size) return true;

    const errorsList = tags.map((tag: string, i: number) => {
      const remainingTags = [...tags];
      remainingTags.splice(i, 1);

      if (!!tag && remainingTags.includes(tag)) {
        return new yup.ValidationError(message, tag, `${pathName}.${i}`);
      }
    });

    return this.createError({ message: () => errorsList.filter(Boolean) });
  });
});

// Maximum of 7 emails validation method
yup.addMethod(yup.array, 'maxNumberEmail', function (message: string, pathName: string) {
  return this.test('maxNumberEmail', message, function (tags: string[] = []) {
    if (tags.length <= MAX_SUPPORT_EMAIL_NUMBER) return true;

    const errorsList = tags.map((tag: string, index: number) => {
      if (index > MAX_SUPPORT_EMAIL_NUMBER - 1) {
        return new yup.ValidationError(message, tag, `${pathName}.${index}`);
      }
    });

    return this.createError({ message: () => errorsList.filter(Boolean) });
  });
});

export const trialRegistrationValidation = yup.object({
  email: yup
    .string()
    .required('validations.missingEmail')
    .max(MAX_CHAR_REGISTER_EMAIL, 'validations.maxCharEmailRegister')
    .matches(emailRegex, 'validations.invalidEmail')
    .majorProviders('validations.majorEmailProviders'),
  termsAndConditions: yup
    .boolean()
    .required('validations.acceptTermsConditions')
    .oneOf([true], 'validations.acceptTermsConditions'),
  name: yup
    .string()
    .required('validations.missingName')
    .max(MAX_CHAR_NAME, 'validations.maxCharName'),
  country: yup.string().required('validations.missingCountry'),
  password: yup
    .string()
    .required('validations.missingPassword')
    .min(MIN_CHAR_PASSWORD, 'validations.minLengthPassword')
    .max(MAX_CHAR_PASSWORD, 'validations.maxLengthPassword')
    .matches(/[a-z]+/, 'validations.requiresLowerCaseCharPassword')
    .matches(/[A-Z]+/, 'validations.requiresUpperCaseCharPassword')
    .matches(/^.*[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~].*$/, 'validations.specialCharPassword')
    .matches(/\d+/, 'validations.requiresNumericCharPassword'),
  confirmPassword: yup
    .string()
    .required('validations.missingConfirmPassword')
    .oneOf([yup.ref('password'), null], 'validations.notMatchingPasswords'),
  recaptcha: yup.string().required('validations.missingRecaptcha').nullable(true),
});

export const supportFormValidation = yup.object({
  name: yup.string().required('').max(MAX_CHAR_COMPANY_NAME, 'validations.maxCharName'),
  emails: yup
    .array()
    .of(
      yup
        .string()
        .email('validations.invalidEmail')
        .required('')
        .max(MAX_CHAR_SUPPORT_EMAIL, 'validations.maxCharSupportEmail')
    )
    .uniqueTag('validations.duplicatedEntries', 'emails')
    .min(1, ' ')
    .maxNumberEmail('validations.maxCountSupportEmail', 'emails'),
  companyName: yup
    .string()
    .required('')
    .max(MAX_CHAR_COMPANY_NAME, 'validations.maxCharCompanyName'),
  issueType: yup.string().required(''),
  severity: yup.string().required(''),
  issueDescription: yup
    .string()
    .required('')
    .max(MAX_CHAR_PROBLEM_DESCRIPTION, 'validations.supportFormProblemDescription'),
  phoneNumber: yup
    .string()
    .required('')
    .test('testPhoneNumber', 'validations.maxCharSupportPhone', function (phoneNumber = '') {
      const phoneNumberExt = `${this.parent?.phoneNumberExt || ''}`;

      return phoneNumber.length + phoneNumberExt.length <= MAX_CHAR_SUPPORT_PHONE;
    }),
  timezone: yup.string().nullable(false),
});
export const bspSupportFormValidation = yup.object({
  firstName: yup.string().required('').max(MAX_CHAR_FIRST_NAME, 'validations.maxCharFirstName'),
  lastName: yup.string().required('').max(MAX_CHAR_LAST_NAME, 'validations.maxCharLastName'),
  email: yup
    .string()
    .required('validations.missingEmail')
    .max(MAX_CHAR_BSP_SUPPORT_EMAIL, 'validations.maxCharBSPSupportEmail')
    .matches(emailRegex, 'validations.invalidEmail'),
  country: yup.string().required('validations.missingCountry'),
});
